import { DateTime } from "luxon";

/**
 * Remembers which guest link this browser opened last, so a page that loses
 * its data can send the guest straight back.
 *
 * The portal's pages carry their data in router state, which is lost when a
 * phone discards a backgrounded tab and reloads it, or when the tab is closed
 * and reopened. With the link kept here, those pages go back through its
 * landing (/go for a booking, /p/:roomId for a property link), which reloads
 * everything and continues straight on.
 *
 * One record for both kinds, so whichever link was opened last wins — an old
 * booking can never hijack a property link or the other way round.
 *
 * - Booking: expires at the end of the check-out day in the property's
 *   timezone, so the link stops reopening once the stay is over.
 * - Property link: has no stay, so it's kept for PROPERTY_MS from the last
 *   time it was opened.
 *
 * It lives in localStorage rather than a cookie: nothing on the server reads
 * it, and the guest's sign-in itself is already persisted by Amplify.
 */
const KEY = "guestStay";
// Used until booking details arrive with the real check-out date.
const PROVISIONAL_MS = 2 * 24 * 60 * 60 * 1000;
const PROPERTY_MS = 30 * 24 * 60 * 60 * 1000;

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    const stay = raw ? JSON.parse(raw) : null;
    if (!stay) return null;
    if (!stay.expiresAt || Date.now() > stay.expiresAt) {
      localStorage.removeItem(KEY);
      return null;
    }
    return stay;
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable; a reload will just land on /go.
  }
};

/** Call as soon as a booking id is known, before details have loaded. */
export const rememberGuestBooking = (bID) => {
  if (!bID) return;
  const current = read();
  // Keep an expiry already worked out from the real check-out date.
  if (current?.bID === bID && current?.final) return;
  write({ bID, expiresAt: Date.now() + PROVISIONAL_MS, final: false });
};

/** Pin the expiry to the end of the check-out day, in the property's zone. */
export const setGuestStayEnd = (bID, checkOutDate, timezone) => {
  if (!bID || !checkOutDate) return;
  const zone = timezone || "America/Los_Angeles";
  const datePart = String(checkOutDate).split("T")[0];
  const end = DateTime.fromISO(datePart, { zone }).endOf("day");
  if (!end.isValid) return;
  write({ bID, expiresAt: end.toMillis(), final: true });
};

/** Call each time a property link (/p/:roomId) opens successfully. */
export const rememberGuestProperty = (roomId) => {
  if (!roomId) return;
  write({ roomId, expiresAt: Date.now() + PROPERTY_MS });
};

export const forgetGuestStay = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
};

/** The remembered property link's room id, or null. */
export const rememberedPropertyId = () => read()?.roomId || null;

/** The remembered booking id, or null once the stay has ended. */
export const rememberedBookingId = () => read()?.bID || null;

/**
 * Where a page that lost its data should go: the remembered property link,
 * or /go for the remembered booking set to continue automatically, or plain
 * /go when nothing is remembered.
 */
export const guestResumePath = () => {
  const stay = read();
  if (stay?.roomId) return `/p/${encodeURIComponent(stay.roomId)}`;
  if (stay?.bID) return `/go?bID=${encodeURIComponent(stay.bID)}&autoStart=1`;
  return "/go";
};
